<script setup>
import {computed, nextTick, onMounted, ref, watch} from 'vue'
import {useElementSize} from '@vueuse/core'
import {ChevronRight} from 'lucide-vue-next'

import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'

const props = defineProps({
  crumbs: { type: Array, required: true },
  places: { type: Array, required: true },
  current: { type: Array, required: true },
  toRoute: { type: Function, required: true },
})

const first = computed(() => props.crumbs[0])
const last = computed(() => props.crumbs[props.crumbs.length - 1])

// Crumbs are measured in an invisible copy, then the middle ones nearest the
// current Place are kept while they fit; the rest collapse into "…".
const nav = ref(null)
const { width: available } = useElementSize(nav)
const measured = []
const ellipsisMeasure = ref(null)
const widths = ref([])
const ellipsisWidth = ref(0)
const GAP = 2

const measure = () => {
  widths.value = props.crumbs.map((_, i) => (measured[i]?.offsetWidth ?? 0) + GAP)
  ellipsisWidth.value = (ellipsisMeasure.value?.offsetWidth ?? 0) + GAP
}

onMounted(() => {
  measure()
  document.fonts?.ready.then(measure)
})
watch(() => props.crumbs, () => nextTick(measure))

const firstShownMiddle = computed(() => {
  const n = props.crumbs.length
  const w = widths.value
  if (n <= 2 || w.length !== n) return 1
  const total = w.reduce((sum, x) => sum + x, 0)
  if (total <= available.value) return 1

  let used = w[0] + w[n - 1] + ellipsisWidth.value
  let start = n - 1
  while (start > 1 && used + w[start - 1] <= available.value) {
    used += w[start - 1]
    start--
  }
  return start
})

const visibleMiddle = computed(() => props.crumbs.slice(firstShownMiddle.value, -1))
const hasHidden = computed(() => firstShownMiddle.value > 1)

const currentKey = computed(() => props.current.join('/'))
const onPath = (place) => currentKey.value === place.slugs.join('/') || currentKey.value.startsWith(`${place.slugs.join('/')}/`)

const open = ref(false)
let closeTimer = null

const show = () => {
  clearTimeout(closeTimer)
  open.value = true
}

const hide = () => {
  clearTimeout(closeTimer)
  closeTimer = setTimeout(() => { open.value = false }, 150)
}
</script>

<template>
  <nav ref="nav" aria-label="Place" class="relative ml-1.5 flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden text-sm">
    <div class="invisible absolute left-0 top-0 flex w-max whitespace-nowrap" aria-hidden="true">
      <span
        v-for="(crumb, i) in crumbs"
        :key="i"
        :ref="el => { measured[i] = el }"
        class="inline-flex shrink-0 items-center gap-0.5"
        :class="i === crumbs.length - 1 ? 'font-semibold' : ''"
      >
        <ChevronRight v-if="i" class="w-3.5 h-3.5" />
        {{ crumb.name }}
      </span>
      <span ref="ellipsisMeasure" class="inline-flex shrink-0 items-center gap-0.5">
        <ChevronRight class="w-3.5 h-3.5" />
        <span class="px-1">…</span>
      </span>
    </div>

    <router-link v-if="first.slugs" :to="toRoute(first.slugs)" class="shrink-0 text-primary hover:underline">
      {{ first.name }}
    </router-link>

    <template v-if="hasHidden">
      <ChevronRight class="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Popover v-model:open="open">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="shrink-0 rounded px-1 text-primary hover:bg-muted"
            aria-label="Show all places"
            @mouseenter="show"
            @mouseleave="hide"
          >
            …
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          class="w-64 max-h-80 overflow-y-auto p-1"
          @mouseenter="show"
          @mouseleave="hide"
        >
          <router-link
            v-for="place in places"
            :key="place.slugs.join('/')"
            :to="toRoute(place.target)"
            class="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
            :class="onPath(place) ? 'font-semibold' : ''"
            :style="{ paddingLeft: `${8 + place.depth * 12}px` }"
            @click="open = false"
          >
            <span class="min-w-0 flex-1 truncate">{{ place.name }}</span>
            <span class="text-xs font-normal text-muted-foreground">{{ place.count }}</span>
          </router-link>
        </PopoverContent>
      </Popover>
    </template>

    <template v-for="crumb in visibleMiddle" :key="`${crumb.slugs?.join('/')}:${crumb.name}`">
      <ChevronRight class="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <router-link v-if="crumb.slugs" :to="toRoute(crumb.slugs)" class="shrink-0 text-primary hover:underline">
        {{ crumb.name }}
      </router-link>
      <span v-else class="shrink-0 text-muted-foreground">{{ crumb.name }}</span>
    </template>

    <ChevronRight class="w-3.5 h-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
    <span class="min-w-0 truncate font-semibold" :title="last.name">{{ last.name }}</span>
  </nav>
</template>
